import React, { useState, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import AgencyTargetingPanel from './AgencyTargetingPanel';

const API_URL = process.env.REACT_APP_API_URL || 'https://videoreel-api-habwcma9cygwhphm.westus-01.azurewebsites.net';
const MAX_BYTES = 500 * 1024 * 1024; // 500 MB
const ALLOWED_EXTS = ['.mp4', '.mov'];

const AGENCY_JWT_KEY = 'agency_jwt';
const getAgencyToken = () => localStorage.getItem(AGENCY_JWT_KEY) || '';

// ─── Styled components ────────────────────────────────────────────────────────

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const slideUp = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.8);
  backdrop-filter: blur(8px);
  z-index: 10000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px 20px;
  overflow-y: auto;
  animation: ${fadeIn} 150ms ease;
`;

const Modal = styled.div`
  background: linear-gradient(160deg, #0f0f19 0%, #1a0a2e 100%);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 20px;
  width: 100%;
  max-width: 560px;
  padding: 32px 28px 28px;
  box-shadow: 0 24px 64px rgba(0,0,0,0.6);
  animation: ${slideUp} 200ms ease;
  color: white;
  margin: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  font-size: 1.2rem;
  font-weight: 700;
  margin: 0;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  color: rgba(255,255,255,0.4);
  font-size: 1.4rem;
  cursor: pointer;
  line-height: 1;
  padding: 0;
  &:hover { color: white; }
`;

const Section = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.div`
  font-size: 0.78rem;
  font-weight: 600;
  color: rgba(255,255,255,0.4);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 14px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255,255,255,0.07);
`;

const Field = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 0.78rem;
  color: rgba(255,255,255,0.55);
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const Input = styled.input`
  width: 100%;
  background: #1a1a2e;
  border: 1px solid ${p => p.$error ? '#ff4757' : 'rgba(255,255,255,0.12)'};
  border-radius: 8px;
  color: white;
  font-size: 0.9rem;
  padding: 10px 12px;
  outline: none;
  box-sizing: border-box;
  &:focus { border-color: #ff4757; }
  &::placeholder { color: rgba(255,255,255,0.25); }
`;

const Textarea = styled.textarea`
  width: 100%;
  background: #1a1a2e;
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 8px;
  color: white;
  font-size: 0.9rem;
  padding: 10px 12px;
  outline: none;
  box-sizing: border-box;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
  &:focus { border-color: #ff4757; }
  &::placeholder { color: rgba(255,255,255,0.25); }
`;

const DropZone = styled.div`
  border: 2px dashed ${p => p.$dragging ? '#ff4757' : p.$file ? 'rgba(46,204,113,0.5)' : 'rgba(255,255,255,0.15)'};
  border-radius: 12px;
  padding: 28px 20px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
  background: ${p => p.$dragging ? 'rgba(255,71,87,0.05)' : p.$file ? 'rgba(46,204,113,0.04)' : 'rgba(255,255,255,0.02)'};
  &:hover { border-color: rgba(255,71,87,0.5); background: rgba(255,71,87,0.04); }
`;

const DropText = styled.div`
  font-size: 0.88rem;
  color: rgba(255,255,255,0.45);
  margin-top: 6px;
`;

const DropHint = styled.div`
  font-size: 0.75rem;
  color: rgba(255,255,255,0.25);
  margin-top: 4px;
`;

const ProgressBar = styled.div`
  height: 4px;
  background: rgba(255,255,255,0.08);
  border-radius: 2px;
  overflow: hidden;
  margin-top: 12px;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${p => p.$pct}%;
  background: #ff4757;
  border-radius: 2px;
  transition: width 0.3s;
`;

const BtnRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
`;

const PrimaryBtn = styled.button`
  flex: 1;
  background: #ff4757;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  padding: 12px;
  cursor: pointer;
  transition: background 0.2s;
  &:hover:not(:disabled) { background: #e0404f; }
  &:disabled { background: #333; color: #555; cursor: default; }
`;

const ErrorMsg = styled.p`
  color: #ff4757;
  font-size: 0.82rem;
  margin: 10px 0 0;
  text-align: center;
`;

const StatusPill = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 0.72rem;
  font-weight: 600;
  background: ${p => {
    if (p.$s === 'approved') return 'rgba(46,204,113,0.12)';
    if (p.$s === 'rejected') return 'rgba(255,71,87,0.12)';
    if (p.$s === 'pending_review') return 'rgba(255,200,0,0.12)';
    return 'rgba(255,255,255,0.06)';
  }};
  color: ${p => {
    if (p.$s === 'approved') return '#2ecc71';
    if (p.$s === 'rejected') return '#ff4757';
    if (p.$s === 'pending_review') return '#ffc800';
    return 'rgba(255,255,255,0.5)';
  }};
  border: 1px solid ${p => {
    if (p.$s === 'approved') return 'rgba(46,204,113,0.3)';
    if (p.$s === 'rejected') return 'rgba(255,71,87,0.3)';
    if (p.$s === 'pending_review') return 'rgba(255,200,0,0.3)';
    return 'rgba(255,255,255,0.12)';
  }};
`;

const EMPTY_TARGETING = {
  target_genders: [],
  target_birth_year_min: null,
  target_birth_year_max: null,
  target_states: [],
  target_employment_statuses: [],
  target_income_ranges: [],
  excluded_publications: [],   // publication IDs this ad should NOT appear in
};

const AgencyVideoUploadModal = ({ onClose, onCreated, defaultTargeting }) => {
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [url, setUrl] = useState('');
  const [points, setPoints] = useState(5);
  const [targeting, setTargeting] = useState(defaultTargeting || EMPTY_TARGETING);
  const [uploadPct, setUploadPct] = useState(0);
  const [phase, setPhase] = useState('form'); // 'form' | 'uploading' | 'saving' | 'done'
  const [error, setError] = useState('');

  const validateFile = (f) => {
    if (!f) return 'No file selected.';
    const ext = '.' + f.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTS.includes(ext)) return 'Only .mp4 and .mov files are supported.';
    if (f.size > MAX_BYTES) return 'File exceeds the 500 MB limit.';
    return null;
  };

  const pickFile = (f) => {
    const err = validateFile(f);
    if (err) { setError(err); return; }
    setError('');
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' '));
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) pickFile(f);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const handleDragLeave = () => setDragging(false);

  const handleSubmit = async (saveAsDraft) => {
    if (!file) { setError('Please select a video file.'); return; }
    if (!title.trim()) { setError('Title is required.'); return; }
    setError('');
    setPhase('uploading');
    try {
      const token = getAgencyToken();

      // 1. Get SAS upload URL
      const urlRes = await fetch(
        `${API_URL}/api/agency/videos/upload-url?filename=${encodeURIComponent(file.name)}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );
      if (!urlRes.ok) throw new Error(`Failed to get upload URL (${urlRes.status})`);
      const { upload_url, public_url } = await urlRes.json();

      // 2. Upload blob directly via XHR (for progress tracking)
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', upload_url);
        xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
        xhr.setRequestHeader('Content-Type', file.type || 'video/mp4');
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setUploadPct(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`)));
        xhr.onerror = () => reject(new Error('Upload network error'));
        xhr.send(file);
      });

      setPhase('saving');

      // 3. Create metadata record
      const createRes = await fetch(`${API_URL}/api/agency/videos`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim() || undefined,
          url: url.trim() || undefined,
          blob_url: public_url,
          targeting,
          show_sponsored: false,
          points: Number(points),
        }),
      });
      if (!createRes.ok) throw new Error(`Failed to save video record (${createRes.status})`);
      const { video_id: createdId } = await createRes.json();

      // 4. If not saving as draft, publish directly (no admin review required)
      if (!saveAsDraft) {
        await fetch(`${API_URL}/api/agency/videos/${createdId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ status: 'approved' }),
        });
      }

      setPhase('done');
      onCreated?.();
    } catch (e) {
      setError(e.message);
      setPhase('form');
    }
  };

  if (phase === 'done') {
    return (
      <Overlay onClick={() => onClose()}>
        <Modal onClick={e => e.stopPropagation()}>
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: '3rem', marginBottom: 16 }}>✅</div>
            <h2 style={{ color: 'white', margin: '0 0 10px', fontSize: '1.2rem' }}>Video published</h2>
            <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.88rem', margin: '0 0 28px' }}>
              Your video is live and will be shown to matching subscribers.
            </p>
            <PrimaryBtn onClick={onClose}>Close</PrimaryBtn>
          </div>
        </Modal>
      </Overlay>
    );
  }

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Upload ad video</ModalTitle>
          <CloseBtn onClick={onClose} aria-label="Close">×</CloseBtn>
        </ModalHeader>

        {/* File picker */}
        <Section>
          <SectionTitle>Video file</SectionTitle>
          <DropZone
            $dragging={dragging}
            $file={!!file}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => document.getElementById('agency-file-input').click()}
          >
            <input
              id="agency-file-input"
              type="file"
              accept=".mp4,.mov,video/mp4,video/quicktime"
              style={{ display: 'none' }}
              onChange={e => e.target.files?.[0] && pickFile(e.target.files[0])}
            />
            {file ? (
              <>
                <div style={{ fontSize: '1.6rem' }}>🎬</div>
                <DropText style={{ color: 'rgba(46,204,113,0.9)' }}>{file.name}</DropText>
                <DropHint>{(file.size / 1024 / 1024).toFixed(1)} MB</DropHint>
              </>
            ) : (
              <>
                <div style={{ fontSize: '1.8rem' }}>📁</div>
                <DropText>Click to select or drag & drop</DropText>
                <DropHint>.mp4 or .mov · max 500 MB</DropHint>
              </>
            )}
          </DropZone>
          {(phase === 'uploading') && (
            <ProgressBar><ProgressFill $pct={uploadPct} /></ProgressBar>
          )}
        </Section>

        {/* Details */}
        <Section>
          <SectionTitle>Details</SectionTitle>
          <Field>
            <Label>Title *</Label>
            <Input
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Give your ad a clear title"
              maxLength={120}
              $error={!title.trim() && !!error}
            />
          </Field>
          <Field>
            <Label>Description</Label>
            <Textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Optional — describe what this ad is for"
              maxLength={500}
            />
          </Field>
          <Field>
            <Label>Advertiser website URL</Label>
            <Input
              value={url}
              onChange={e => setUrl(e.target.value)}
              placeholder="https://example.com"
              maxLength={500}
              type="url"
            />
          </Field>
          <Field>
            <Label>Points rewarded per view</Label>
            <input
              type="number"
              min={1}
              max={100}
              value={points}
              onChange={e => setPoints(Math.max(1, Math.min(100, Number(e.target.value) || 5)))}
              style={{ width: 80, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#fff', padding: '8px 12px', fontSize: '0.95rem' }}
            />
            <span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.35)', marginTop: 4 }}>How many points subscribers earn for watching this ad (1–100)</span>
          </Field>
        </Section>

        {/* Targeting */}
        <Section>
          <SectionTitle>Targeting criteria</SectionTitle>
          <AgencyTargetingPanel value={targeting} onChange={setTargeting} />
        </Section>

        {error && <ErrorMsg>{error}</ErrorMsg>}

        <BtnRow>
          <PrimaryBtn
            onClick={() => handleSubmit(false)}
            disabled={phase === 'uploading' || phase === 'saving' || !file || !title.trim()}
          >
            {phase === 'uploading'
              ? `Uploading… ${uploadPct}%`
              : phase === 'saving'
              ? 'Saving…'
              : 'Upload & publish'}
          </PrimaryBtn>
        </BtnRow>
      </Modal>
    </Overlay>
  );
};

export { StatusPill };
export default AgencyVideoUploadModal;
